// import React, { useState, useRef, useCallback } from "react";
// import { Modal, Button } from "react-bootstrap";
// import Cropper from "react-easy-crop";
// import { FaCheck, FaSearchMinus, FaSearchPlus, FaCrop } from "react-icons/fa";

// const ImageCropperModal = ({
//     show,
//     imageUrl,
//     type,
//     onClose,
//     onCropped
// }) => {
//     const cropperRef = useRef(null);

//     const CATEGORY_IMAGE_SIZE = window.imageConfig?.CATEGORY_IMAGE_SIZE || { width: 50, height: 50 };

//     const targetSize = CATEGORY_IMAGE_SIZE;
//     const aspectRatio = targetSize.width / targetSize.height;

//     // easy crop states
//     const [crop, setCrop] = useState({ x: 0, y: 0 });
//     const [zoom, setZoom] = useState(1);
//     const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

//     // When crop is done, store crop area in pixels
//     const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
//         setCroppedAreaPixels(croppedAreaPixels);
//     }, []);

//     // Convert cropped area to file (output size is EXACT based on config)
//     const getCroppedImageBlob = async () => {
//         const image = await createImage(imageUrl);
//         const canvas = document.createElement("canvas");
//         const ctx = canvas.getContext("2d");

//         canvas.width = targetSize.width;
//         canvas.height = targetSize.height;

//         ctx.drawImage(
//             image,
//             croppedAreaPixels.x,
//             croppedAreaPixels.y,
//             croppedAreaPixels.width,
//             croppedAreaPixels.height,
//             0,
//             0,
//             targetSize.width,
//             targetSize.height
//         );

//         return new Promise((resolve) => {
//             canvas.toBlob(
//                 (blob) => resolve(blob),
//                 "image/jpeg",
//                 1
//             );
//         });
//     };

//     const handleApplyCrop = async () => {
//         if (!croppedAreaPixels) return;

//         const blob = await getCroppedImageBlob();
//         const file = new File([blob], `cropped-${type}.jpg`, { type: "image/jpeg" });

//         onCropped(file);
//         onClose();
//     };

//     // Utility: Create image from URL
//     const createImage = (url) =>
//         new Promise((resolve, reject) => {
//             const img = new Image();
//             img.addEventListener("load", () => resolve(img));
//             img.addEventListener("error", (err) => reject(err));
//             img.src = url;
//         });

//     return (
//         <>
//             <Modal show={show} onHide={onClose} size="xl" backdrop="static" centered>
//                 <Modal.Header closeButton>
//                     <Modal.Title className="d-flex justify-content-between align-items-center w-100">
//                         <span>
//                             <FaCrop className="me-2" />
//                             Crop <strong>Category Image</strong>
//                         </span>

//                         <div className="d-flex align-items-center gap-3 px-4">
//                             <span>{(zoom * 100).toFixed(0)}%</span>
//                             <Button
//                                 variant="outline-secondary"
//                                 size="sm"
//                                 onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
//                             >
//                                 <FaSearchMinus />
//                             </Button>
//                             <Button
//                                 variant="outline-secondary"
//                                 size="sm"
//                                 onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
//                             >
//                                 <FaSearchPlus />
//                             </Button>
//                         </div>
//                     </Modal.Title>
//                 </Modal.Header>

//                 <Modal.Body>
//                     {imageUrl && (
//                         <div style={{ position: "relative", width: "100%", height: "70vh" }}>
//                             <Cropper
//                                 image={imageUrl}
//                                 crop={crop}
//                                 zoom={zoom}
//                                 aspect={aspectRatio}
//                                 onCropChange={setCrop}
//                                 onZoomChange={setZoom}
//                                 onCropComplete={onCropComplete}
//                                 cropShape="rect"
//                                 showGrid={false}
//                                 objectFit="contain"
//                             />
//                         </div>
//                     )}
//                 </Modal.Body>

//                 <Modal.Footer>
//                     <Button variant="secondary" onClick={onClose}>
//                         Cancel
//                     </Button>

//                     <Button
//                         variant="primary"
//                         disabled={!croppedAreaPixels}
//                         onClick={handleApplyCrop}
//                     >
//                         <FaCheck className="me-2" /> Apply
//                     </Button>
//                 </Modal.Footer>
//             </Modal>
//         </>
//     );
// };

// export default ImageCropperModal;

import React, { useState, useRef, useCallback, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Cropper from "react-easy-crop";
import { FaCheck, FaSearchMinus, FaSearchPlus, FaCrop } from "react-icons/fa";

/**
 * 🔧 Crop configuration by type
 * Add new types here – NO component changes needed
 */
const CROP_CONFIG = {
  "category-image": {
    title: "Category Image",
    size: window.imageConfig?.CATEGORY_IMAGE_SIZE || { width: 50, height: 50 },
    quality: 1,
  },
  "category-banner": {
    title: "Category Banner",
    size: window.imageConfig?.CATEGORY_BANNER_SIZE || {
      width: 1920,
      height: 1080,
    },
    quality: 0.95,
  },
  "category-group-image": {
    title: "Category Group Image",
    size: window.imageConfig?.CATEGORY_GROUP_IMAGE_SIZE || {
      width: 50,
      height: 50,
    },
    quality: 1,
  },
  "category-group-banner": {
    title: "Category Group Banner",
    size: window.imageConfig?.CATEGORY_GROUP_BANNER_SIZE || {
      width: 1920,
      height: 1080,
    },
    quality: 0.95,
  },
  "product-image": {
    title: "Product Image",
    size: { width: 600, height: 600 },
    quality: 0.95,
  },
  "profile-image": {
    title: "Profile Image",
    size: { width: 300, height: 300 },
    quality: 0.9,
  },
};

const ImageCropperModal = ({
  show,
  imageUrl,
  type = "category-image",
  onClose,
  onCropped,
}) => {
  const cropperRef = useRef(null);

  // 🔹 Pick config based on type
  const cropConfig = CROP_CONFIG[type] || CROP_CONFIG["category-image"];
  const targetSize = cropConfig.size;
  const aspectRatio = targetSize.width / targetSize.height;

  // Crop states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Reset when modal opens or image/type changes
  useEffect(() => {
    if (show) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [show, imageUrl, type]);

  // Store cropped area in pixels
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Convert cropped area to resized image blob
  const getCroppedImageBlob = async () => {
    const image = await createImage(imageUrl);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = targetSize.width;
    canvas.height = targetSize.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      targetSize.width,
      targetSize.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", cropConfig.quality);
    });
  };

  // Apply crop
  const handleApplyCrop = async () => {
    if (!croppedAreaPixels) return;

    const blob = await getCroppedImageBlob();
    const file = new File([blob], `cropped-${type}-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });

    onCropped(file);
    onClose();
  };

  // Utility: create image object
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

  return (
    <Modal show={show} onHide={onClose} size="xl" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex justify-content-between align-items-center w-100">
          <span>
            <FaCrop className="me-2" />
            Crop <strong>{cropConfig.title}</strong>
          </span>

          <div className="d-flex align-items-center gap-3 px-4">
            <span>{Math.round(zoom * 100)}%</span>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
            >
              <FaSearchMinus />
            </Button>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
            >
              <FaSearchPlus />
            </Button>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {imageUrl && (
          <div style={{ position: "relative", width: "100%", height: "70vh" }}>
            <Cropper
              ref={cropperRef}
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              cropShape="rect"
              showGrid={false}
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="primary"
          disabled={!croppedAreaPixels}
          onClick={handleApplyCrop}
        >
          <FaCheck className="me-2" /> Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCropperModal;
